// Kampf am Ufer (§6): ein Kampf, der live läuft (K46). Die Darstellung lässt die Simulation ein Stück vorauslaufen, damit
// Ausholen und Würfe rechtzeitig beginnen. Kommt ein Befehl, springt der Kampf auf den letzten Schnappschuss vor dessen
// Takt zurück und rechnet mit dem Befehl neu. Weil alles deterministisch ist, ergibt das genau denselben Kampf wie
// runBattle mit derselben Befehlsliste (Tests, Harness, Wiederholung).
import { createRng } from '../rng.ts';
import type { BattleCommand, BattleOptions, BattleSetup, Unit } from '../types.ts';
import type { Battle } from './core.ts';
import { EPS } from './core.ts';
import { createBattle, stepBattle } from '../battle.ts';

/** Alles, was sich an einem Kampf ändert, als Kopie; Ereignisse und Bilder werden nur angehängt, darum genügt ihre Länge */
type Snap = {
  tick: number;
  t: number;
  rng: number;
  units: Unit[];
  events: number;
  frames: number;
  rest: Pick<Battle, 'rainMultiplier' | 'duelSince' | 'nextGust' | 'gust' | 'gustAngle' | 'nextLightning' | 'strikes' | 'farewells' | 'retreating' | 'outcome' | 'via' | 'bounces' | 'shots' | 'nextShotId' | 'nextTelegraphId'>;
};

export type Live = {
  b: Battle;
  commands: BattleCommand[];
  snaps: Snap[];
  /** alle so viele Takte ein Schnappschuss */
  every: number;
};

const tickOf = (b: Battle) => Math.round(b.t / b.rules.tickSeconds);

function snapshot(b: Battle): Snap {
  return {
    tick: tickOf(b),
    t: b.t,
    rng: b.rng.state(),
    units: structuredClone(b.units),
    events: b.events.length,
    frames: b.frames.length,
    rest: structuredClone({
      rainMultiplier: b.rainMultiplier,
      duelSince: b.duelSince,
      nextGust: b.nextGust,
      gust: b.gust,
      gustAngle: b.gustAngle,
      nextLightning: b.nextLightning,
      strikes: b.strikes,
      farewells: b.farewells,
      retreating: b.retreating,
      outcome: b.outcome,
      via: b.via,
      bounces: b.bounces,
      shots: b.shots,
      nextShotId: b.nextShotId,
      nextTelegraphId: b.nextTelegraphId,
    }),
  };
}

function restore(live: Live, s: Snap): void {
  const b = live.b;
  b.t = s.t;
  b.rng = createRng(s.rng);
  b.units = structuredClone(s.units);
  b.events.length = s.events;
  b.frames.length = s.frames;
  Object.assign(b, structuredClone(s.rest));
  b.commands = live.commands.filter((c) => c.tick > s.tick);
}

export function createLive(setup: BattleSetup, seed: number, options: BattleOptions = {}, every = 10): Live {
  const commands = [...(options.commands ?? [])].sort((a, c) => a.tick - c.tick);
  const b = createBattle(setup, seed, { ...options, recordFrames: true, commands });
  return { b, commands, snaps: [snapshot(b)], every };
}

/** Bis zur Zeit t rechnen (oder bis der Kampf vorbei ist) */
export function liveAdvance(live: Live, t: number): void {
  const b = live.b;
  while (!b.outcome && b.t + EPS < t) {
    stepBattle(b);
    if (tickOf(b) % live.every === 0) live.snaps.push(snapshot(b));
  }
}

/**
 * K46: ein Befehl ab seinem Takt. Liegt der Takt schon hinter der Simulation, rechnet sie ab dem letzten Schnappschuss
 * davor neu, bis zur Zeit, bis zu der sie schon war
 */
export function liveCommand(live: Live, cmd: BattleCommand): void {
  const tick = Math.max(1, cmd.tick);
  const c = { ...cmd, tick };
  let i = live.commands.length;
  while (i > 0 && live.commands[i - 1]!.tick > tick) i--;
  live.commands.splice(i, 0, c);
  const b = live.b;
  if (tick > tickOf(b)) {
    // Noch nicht gerechnet: einfach einreihen
    b.commands = live.commands.filter((x) => x.tick > tickOf(b));
    return;
  }
  const until = b.t;
  let k = live.snaps.length - 1;
  while (k > 0 && live.snaps[k]!.tick >= tick) k--;
  live.snaps.length = k + 1;
  restore(live, live.snaps[k]!);
  liveAdvance(live, until);
}

/** Der Takt, ab dem ein Befehl gilt, den man bei der Anzeigezeit t gibt */
export const commandTick = (live: Live, t: number): number => Math.max(1, Math.floor(t / live.b.rules.tickSeconds + EPS) + 1);
